import { type DuaPartType } from '@components/DuaPage/DuaPageComponent'
import DuaReaderContainer from '@components/DuaPage/DuaReaderContainer'
import Breadcrumb from '@components/shared/Breadcrumb/Breadcrumb'
import { DefaultSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  getNativeDuaPage,
  hasNativeDuaBridge,
  type NativeDuaPage,
} from 'helpers/nativeDuaBridge'
import {
  getCevsenGroupedPageForBab,
  getCevsenPage,
  getCevsenRouteForBab,
} from 'utils/getCevsenLocalData'
import { cevsenPageBabMapping } from '.'

type CevsenPageProps = {
  data: {
    [key: string]: DuaPartType[]
  }
  redirectUrl?: string
}

const CevsenPage: FC<CevsenPageProps> = ({ data, redirectUrl }) => {
  const router = useRouter()
  const page = Number(router.query.page)
  const mobileReader = hasNativeDuaBridge()
  const [nativePage, setNativePage] = useState<NativeDuaPage | null>(null)

  useEffect(() => {
    if (redirectUrl) {
      router.push(redirectUrl)
    }
    return () => {}
  }, [page])

  useEffect(() => {
    let isCancelled = false
    setNativePage(null)

    if (
      redirectUrl ||
      !Number.isInteger(page) ||
      page < 1 ||
      page > 10 ||
      !hasNativeDuaBridge()
    ) {
      return () => {
        isCancelled = true
      }
    }

    void getNativeDuaPage('cevsen', page).then((pageData) => {
      if (!isCancelled && pageData?.view === 'grouped') {
        setNativePage(pageData)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [page, redirectUrl])

  const getCevsenBabName = useCallback(
    () =>
      cevsenPageBabMapping.find((item) => item.page === page)?.name ||
      'Cevşen-i Şerif Arapça ve Türkçe Mealli Oku | Hayırhah',
    [page],
  )

  const breadcrumbItems = [
    { label: 'Dualar', href: '/dualar' },
    { label: 'Cevşen-i Şerif', href: '/cevsen' },
    { label: `${getCevsenBabName()}` },
  ]

  const readerData =
    nativePage?.page === page && nativePage.view === 'grouped' ? nativePage.data : data

  return (
    <>
      <DefaultSeo
        title={`${getCevsenBabName()} - Cevşen-i Şerif Arapça ve Türkçe Mealli Oku | Hayırhah`}
        description={`Cevşen-i Şerif ${page} Sayfa Arapça ve Türkçe Mealli Oku. Cevşen-i Şerif duasını uygulamamızda Türkçe ve Arapça Okunuşu ve Anlamı ile birlikte okuyabilirsiniz. Duaları okurken kaldığınız sayfayı işaretleyip tekrar açtığınızda kaldığınız sayfadan devam edebilirsiniz.`}
      />
      <div>
        <Breadcrumb items={breadcrumbItems} />
        <DuaReaderContainer
          dua={{ name: 'Cevşen-i Şerif', type: 'cevsen', pageCount: 10 }}
          page={page}
          data={readerData || {}}
          basePath=""
          mobileReader={mobileReader}
        />
      </div>
    </>
  )
}

export async function getStaticPaths() {
  const arrayOfNumbers = Array.from(Array(10).keys())

  const paths = arrayOfNumbers.map((n) => ({
    params: { page: String(n + 1) },
  }))

  return { paths, fallback: true }
}

export const getStaticProps = async ({
  params,
}: {
  params: { page: string }
}): Promise<{
  props: {
    data: {
      [key: string]: DuaPartType[]
    }
    redirectUrl?: string
  }
}> => {
  let redirectPage = Number(params.page)

  if (redirectPage > 10) {
    const groupedPage = getCevsenGroupedPageForBab(redirectPage)
    redirectPage = groupedPage || 10

    return {
      props: {
        data: {},
        redirectUrl:
          getCevsenRouteForBab(Number(params.page)) || `/cevsen/${redirectPage}`,
      },
    }
  }

  const data = getCevsenPage(params.page) || {}
  return { props: { data } }
}

export default CevsenPage
